import { ActionList, Button, ButtonGroup, Popover } from "@shopify/polaris";
import { ChevronDownIcon } from "@shopify/polaris-icons";
import React, { useCallback, useState } from "react";
import { softAssertNumber } from "../../../helper_functions/utils";
import SetAllQuantitiesModal from "./SetAllQuantitiesModal";
import RecommendationIncreaseModal from "./RecommendationIncreaseModal";
import FillQuantitiesModal from "./FillQuantitiesModal";

export default function CopyRecommendationsButton({
  disabled,
  handleAutofill,
  copyBackorders,
  setAllQuantities,
  setFillQuantities,
}: {
  readonly disabled: boolean;
  readonly handleAutofill: (options?: { percentIncrease?: number; quantityIncrease?: number }) => void;
  readonly copyBackorders: () => void;
  readonly setAllQuantities: (quantity: number) => void;
  readonly setFillQuantities: (quantity: number) => void;
}): React.ReactElement {
  const [active, setActive] = useState<boolean>(false);

  const toggleActive = useCallback(() => {
    setActive(!active);
  }, [active]);

  const closePopover = useCallback(() => {
    setActive(false);
  }, [setActive]);

  const [recommendationIncreaseModalOpen, setRecommendationIncreaseModalOpen] = useState<boolean>(false);

  const closeRecommendationIncreaseModal = useCallback(() => {
    setRecommendationIncreaseModalOpen(false);
  }, [setRecommendationIncreaseModalOpen]);

  const openRecommendationIncreaseModal = useCallback(() => {
    setRecommendationIncreaseModalOpen(true);
  }, [setRecommendationIncreaseModalOpen]);

  const [setAllQuantitiesModalOpen, setSetAllQuantitiesModalOpen] = useState<boolean>(false);

  const closeSetAllQuantitiesModal = useCallback(() => {
    setSetAllQuantitiesModalOpen(false);
  }, [setSetAllQuantitiesModalOpen]);

  const openSetAllQuantitiesModal = useCallback(() => {
    setSetAllQuantitiesModalOpen(true);
  }, [setSetAllQuantitiesModalOpen]);

  const [fillQuantitiesModalOpen, setFillQuantitiesModalOpen] = useState<boolean>(false);
  
  const closeFillQuantitiesModal = useCallback(() => {
    setFillQuantitiesModalOpen(false);
  }, [setFillQuantitiesModalOpen]);
  
  const openFillQuantitiesModal = useCallback(() => {
    setFillQuantitiesModalOpen(true);
  }, [setFillQuantitiesModalOpen]);

  const [increaseByPercentage, setIncreaseByPercentage] = useState<number | undefined>(undefined);
  const [increaseByQuantity, setIncreaseByQuantity] = useState<number | undefined>(undefined);

  const handleIncreaseByPercentageChange = useCallback(
    (value: string) => {
      setIncreaseByPercentage(softAssertNumber(value));
    },
    [setIncreaseByPercentage],
  );

  const handleIncreaseByQuantityChange = useCallback(
    (value: string) => {
      setIncreaseByQuantity(softAssertNumber(value));
    },
    [setIncreaseByQuantity],
  );

  const [allQuantity, setAllQuantity] = useState<number | undefined>(undefined);

  const handleSetAllQuantitiesChange = useCallback(
    (value: string) => {
      setAllQuantity(softAssertNumber(value));
    },
    [setAllQuantity],
  );

  const [fillQuantity, setFillQuantity] = useState<number | undefined>(undefined);

  const handleFillQuantityChange = useCallback(
    (value: string) => {
      setFillQuantity(softAssertNumber(value));
    },
    [setFillQuantity],
  );

  return (
    <>
      <RecommendationIncreaseModal
        closeRecommendationIncreaseModal={closeRecommendationIncreaseModal}
        handleAutofill={handleAutofill}
        handleIncreaseByPercentageChange={handleIncreaseByPercentageChange}
        handleIncreaseByQuantityChange={handleIncreaseByQuantityChange}
        increaseByPercentage={increaseByPercentage}
        increaseByQuantity={increaseByQuantity}
        recommendationIncreaseModalOpen={disabled ? false : recommendationIncreaseModalOpen}
      />

      <SetAllQuantitiesModal
        allQuantity={allQuantity}
        closeSetAllQuantitiesModal={closeSetAllQuantitiesModal}
        handleSetAllQuantitiesChange={handleSetAllQuantitiesChange}
        setAllQuantities={setAllQuantities}
        setAllQuantitiesModalOpen={disabled ? false : setAllQuantitiesModalOpen}
      />

      <FillQuantitiesModal
        closeFillQuantitiesModal={closeFillQuantitiesModal}
        fillQuantitiesModalOpen={disabled ? false : fillQuantitiesModalOpen}
        fillQuantity={fillQuantity}
        handleFillQuantityChange={handleFillQuantityChange}
        setFillQuantities={setFillQuantities}
      />

      <ButtonGroup variant="segmented">
        <Button disabled={disabled} onClick={handleAutofill}>
          Copy recommendations
        </Button>

        <Popover
          activator={<Button accessibilityLabel="Other save actions" icon={ChevronDownIcon} onClick={toggleActive} />}
          active={disabled ? false : active}
          autofocusTarget="first-node"
          onClose={closePopover}
          preferredAlignment="right"
        >
          <ActionList
            actionRole="menuitem"
            items={[
              {
                content: "Copy with increase...",
                disabled,
                helpText: "Increase the recommendations by a percentage and / or a fixed quantity.",
                onAction: openRecommendationIncreaseModal,
              },
              {
                content: "Order back-orders",
                disabled,
                helpText: "Order enough to bring negative available quantities back up to 0.",
                onAction: () => {
                  copyBackorders();
                  closePopover();
                },
              },
              {
                content: "Set all quantities...",
                helpText: "Set all quantities to a fixed value",
                onAction: openSetAllQuantitiesModal,
              },
              {
                content: "Fill all quantities...",
                disabled,
                helpText: "Order enough to bring all available quantities up to a fixed value",
                onAction: openFillQuantitiesModal,
              }
            ]}
          />
        </Popover>
      </ButtonGroup>
    </>
  );
}
